import { Outlet, useNavigate } from "react-router";
import useDeviceDetection, { DEVICE_ORIENTATION, DEVICE_TYPE } from "hook/useDeviceDetection";
import Navbar from "parts/navbar/navbar";
import Topbar from "parts/topbar/topbar";
import { useEffect, useRef, useState } from "react";
import { OAuth2Service } from "services/oauth2.service";
import queryString from "query-string";
import { setData } from "store/user.slice";
import { useDispatch } from "react-redux";
import i18n from 'translation/i18n';
import { themes } from 'themes/themes'
import { setTheme } from "store/theme.slice";
import { useSelector } from "react-redux";
import { themesType } from "themes/themes";
import Modal, { type ModalType } from "parts/modal/modal";
import { useWebsocketsService } from "hook/useWebsockets";
import modalUtil, { sanitizeId } from "utils/modal.util";
import Loader from "components/loader/loader";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getOrchardUrlFromEnv, platforms } from "utils/environment.util";
import { useDeepLink } from "hook/useDeepLink";
import { usePageTitle } from "hook/usePageTitle";

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const { device, orientation } = useDeviceDetection();
  const { authenticatedUser, lastMessage } = useWebsocketsService();
  const searchParams = queryString.parse(window.location.search);
  const code = searchParams['code'];
  const error = searchParams['error'];
  const currentRefreshToken = sessionStorage.getItem('refreshToken');
  const accessToken = sessionStorage.getItem('accessToken');
  const user = useSelector((state: any) => state.user);
  const theme = useSelector((state: any) => state.theme);
  const dispatch = useDispatch();
  const contentRef = useRef<HTMLDivElement>(null);
  const oauth2Service = new OAuth2Service();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useDeepLink();
  usePageTitle();

  type MessageItem =
    | { action: MessageAction.Reminder; title: string }
    | { action: MessageAction.Delete; title: string }
    | { action: MessageAction.Error; title: string };

  enum MessageAction {
    Reminder = "reminder",
    Delete = "delete",
    Error = "error"
  }

  const updateCurrentUser = (async () => {
    try {
      const currentUser = await oauth2Service.getCurrentUser();

      sessionStorage.setItem('user', JSON.stringify(currentUser));
      dispatch(setData({
        lastname: currentUser.last_name,
        firstname: currentUser.first_name,
        isStudent: currentUser.is_student
      }));

    } catch (error) {
      console.log("updateCurrentUser", error);
    }
  });

  const getAuth = (async (refresh: boolean) => {
    if ((code && typeof code === "string") || currentRefreshToken) {
      try {
        const { accessToken, expiresIn, refreshToken } = await oauth2Service.getAuth({
          code: code && typeof code === "string" ? code : undefined,
          refreshToken: currentRefreshToken ?? undefined,
        });
        sessionStorage.setItem('accessToken', accessToken);
        sessionStorage.setItem('expiresIn', expiresIn.toString());
        sessionStorage.setItem('refreshToken', refreshToken);

        window.history.pushState({}, document.title, "/" + "#/");
        setAuthenticated(true);

      } catch (error) {
        console.log("getAuth", error);
      }
    }
  });

  const isAccessTokenValid = () => {
    if (!accessToken) return false;

    try {
      const decodedToken = JSON.parse(atob(accessToken.split('.')[1]));
      const expireAt = decodedToken.iat + Number(sessionStorage.getItem('expiresIn'));
      const now = Math.floor(Date.now() / 1000);

      return now < expireAt;
    } catch (e) {
      return false;
    }
  }

  const redirectNotAllowedUser = () => {
    const orchard = getOrchardUrlFromEnv(process.env.REACT_APP_ENV_VAR ?? "");
    if (process.env.REACT_APP_PLATFORM === platforms.WEB)
      window.location.href = orchard;
    else {
      navigate('disconnected');
      const modalData: ModalType = {
        id: sanitizeId(t("not_allowed")),
        title: t("not_allowed"),
        text: [`${t("not_allowed_text")}`],
        status: "neutre",
        position: "middle",
        blocking: true,
        buttons: [
          <Button key="reconnect" className="btn_secondary" onClick={() => oauth2Service.authorize(true)}>
            {t('reconnect')}
          </Button>,
          <Button key="tactileo" className="btn_primary" onClick={() => window.location.href = orchard}>
            {t('redirect_tactileo')}
          </Button>
        ],
        autoClose: false,
      };
      modalUtil.show(modalData, dispatch);
    }
  }

  useEffect(() => {

    if (error) {
      if (error === 'access_denied') 
        return redirectNotAllowedUser();
    }

    if (accessToken) {
      if (isAccessTokenValid()) {
        setAuthenticated(true);
        updateCurrentUser();
      } else {
        getAuth(true);
      }
      return;
    }

    if (!authenticated && code) {
      getAuth(false);
      return;
    }

    if (!authenticated && !code) {
      process.env.REACT_APP_PLATFORM === platforms.WEB ?
        oauth2Service.authorize() :
        navigate('disconnected');
      return;
    }

  }, [code, accessToken, error])

  useEffect(() => {
    dispatch(setTheme(
      {
        ...themes[process.env.REACT_APP_THEME as keyof themesType],
        isTabletPortraitOrMobile: (device === DEVICE_TYPE.TABLET && orientation === DEVICE_ORIENTATION.PORTRAIT) || device === DEVICE_TYPE.MOBILE
      }
    ));

    localStorage.setItem('theme', JSON.stringify(themes[process.env.REACT_APP_THEME as keyof themesType]))
  }, [device, orientation]);

  useEffect(() => {
    const lang = localStorage.getItem('lang') ?? theme.lang;
    i18n.changeLanguage(lang)
  }, [theme]);

  useEffect(() => {
    authenticatedUser(authenticated);
  }, [authenticated]);

  useEffect(() => {
    const interval = setInterval(async () => {

      if (authenticated) {
        const isValid = await oauth2Service.checkUserSessionIsStillValid();
        if (!isValid) {
          navigate('disconnected');
          const modalData: ModalType = {
            id: sanitizeId(t("disconnection")),
            title: t("disconnection"),
            text: [`${t("disconnection_text")}`],
            status: "neutre",
            position: "middle",
            blocking: true,
            buttons: [
              <Button key="reconnect" className="btn_primary" onClick={() => oauth2Service.authorize(true)}>
                {t('reconnect')}
              </Button>
            ],
            autoClose: false,
          };
          modalUtil.show(modalData, dispatch);

        }
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [])

  useEffect(() => {
    if (lastMessage) {
      const { id, success, data } = JSON.parse(lastMessage.data);

      if (id === "purge" && data.length > 0) {

        const purgeReport = data.map((item: MessageItem) => {
          switch (item.action) {
            case MessageAction.Reminder:
              return `${t("purge_reminder")} ${item.title}`;
            case MessageAction.Delete:
              return `${t("purge_delete_1")} ${item.title} ${t("purge_delete_2")}`;
            case MessageAction.Error:
              return `${t("purge_error")} ${item.title}`;
            default:
              return null;
          }
        });

        const modalData: ModalType = {
          id: sanitizeId(t("purge_title")),
          title: t("purge_title"),
          text: purgeReport,
          status: success ? "neutral" : "error",
          position: "bottomRight",
          blocking: true,
          buttons: [],
          autoClose: false,
        };

        modalUtil.show(modalData, dispatch);
      }
    }
  }, [lastMessage]);

  return authenticated && user.isStudent ?
    (
      <main className={`${theme.isTabletPortraitOrMobile ? "isTabletPortraitOrMobile" : ""} ${theme.class} ${localStorage.getItem('subTheme') ?? 'peacock'}`}>
        <Navbar contentRef={contentRef} />
        <div id="main-content" tabIndex={-1} className="bg-zinc-50">
          <Topbar ref={contentRef} />
          <Outlet />
        </div>
        <Modal />
      </main>
    ) : (
      <Loader className="dark text-white" />
    );
}

export default App;


